import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type { ISiteOption } from "@/customTypings/tipper/Tipper";
import type { ISite, SiteCreatePayload, SiteUpdatePayload } from "@/customTypings/site/Site";
import type { SiteRestoreDeletedPayload } from "@/utils/site/siteCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { postSiteCreate } from "@/utils/site/siteCreateConflict";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

export const sitesService = {
    listForOrganisation,
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function listForOrganisation() {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/sites/ForOrganisation`, requestOptions).then(handleResponse) as Promise<ISiteOption[]>;
}

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/sites/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<ISite>;
}

function create(payload: SiteCreatePayload) {
    return postSiteCreate<ISite>(`/api/sites/create`, payload);
}

function restoreDeletedWithCreateValues(payload: SiteRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/sites/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<ISite>;
}

function update(payload: SiteUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/sites/update`, requestOptions).then(handleResponse) as Promise<
        UpdateMutationResult<ISite>
    >;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/sites/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(handleResponse);
}
